/*
 * AssistantSettingsPanel: Administration, Assistant. Each customer turns Ada on
 * with its own Anthropic API key, so its questions are answered, and billed, on
 * its own account.
 *
 * The key is kept on the Weavian server, never on a box, and never shown again
 * once saved: the screen names it by its last four characters. Nothing is set
 * on the boxes: each signs its questions with its own identity, and the server
 * answers them on the key of the customer the box is assigned to
 * (server/pkg/agentproxy, server/pkg/assistant).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiCheckCircle, FiCpu, FiKey } from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useConfirm } from "./ConfirmModal";
import "./AssistantSettingsPanel.css";

// Saving checks the key with Anthropic first, which takes a moment.
const SAVE_MS = 25000;

export default function AssistantSettingsPanel({ serverApiUrl }) {
  const [domains, setDomains] = useState(null);
  const [error, setError] = useState("");
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  useEffect(() => {
    (async () => {
      const res = await fetchWithTimeout(serverApiUrl("/assistant"), { credentials: "same-origin" }).catch(() => null);
      if (!mounted.current) return;
      if (!res) setError("Could not reach the Weavian server.");
      else if (res.status === 404) setError("This Weavian server does not offer Ada's settings yet.");
      else if (!res.ok) setError(`The Weavian server answered ${res.status}.`);
      else setDomains((await res.json()).domains || []);
    })();
  }, [serverApiUrl]);

  // send makes one change and puts the customer back as the server now holds
  // it. A refusal carries the server's own sentence.
  const send = useCallback(
    async (id, path, method, body, timeoutMs = 10000) => {
      const res = await fetchWithTimeout(
        serverApiUrl(`/assistant/${encodeURIComponent(id)}/${path}`),
        {
          method,
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: body ? JSON.stringify(body) : undefined,
        },
        timeoutMs,
      ).catch(() => null);
      if (!res) return { ok: false, error: "Could not reach the Weavian server." };
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: data.error || `The Weavian server refused this (${res.status}).` };
      if (mounted.current) setDomains((ds) => ds.map((d) => (d.id === data.id ? data : d)));
      return { ok: true };
    },
    [serverApiUrl],
  );

  if (!domains) {
    return (
      <div className="panel-card as-panel">
        <p className="as-quiet">{error || "Reading Ada's settings…"}</p>
      </div>
    );
  }
  if (domains.length === 0) {
    return (
      <div className="panel-card as-panel">
        <p className="as-quiet">There are no customers yet. Ada is turned on for a customer, on its own key.</p>
      </div>
    );
  }
  return (
    <div className="as-stack">
      {domains.map((d) => (
        <Customer key={d.id} d={d} send={send} />
      ))}
    </div>
  );
}

function Customer({ d, send }) {
  const confirm = useConfirm();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    const r = await send(d.id, "key", "PUT", { key }, SAVE_MS);
    setBusy(false);
    if (r.ok) setKey("");
    setMsg(r.ok ? { ok: true, text: "Saved. Anthropic accepted the key, and Ada now answers on it." } : { ok: false, text: r.error });
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Turn Ada off for ${d.name}?`,
      message: `Ada stops answering on ${d.name}'s sites until a key is added again. The key is deleted from the Weavian server.`,
      confirmLabel: "Remove the key",
      variant: "danger",
    });
    if (!ok) return;
    const r = await send(d.id, "key", "DELETE");
    setMsg(r.ok ? { ok: true, text: "Removed. Ada is off for this customer." } : { ok: false, text: r.error });
  };

  const added = d.key_added_at ? new Date(d.key_added_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";
  return (
    <section className="panel-card as-panel" aria-labelledby={`as-${d.id}`}>
      <div className={`as-head ${d.on ? "as-on" : "as-off"}`}>
        {d.on ? <FiCheckCircle aria-hidden="true" /> : <FiCpu aria-hidden="true" />}
        <div>
          <h2 id={`as-${d.id}`}>{d.on ? `Ada is on for ${d.name}` : `Ada is off for ${d.name}`}</h2>
          <p>
            {d.on
              ? `Ada answers on ${d.name}'s own Anthropic account, with the key ending …${d.key_ending}, added by ${d.key_added_by} on ${added}.`
              : `Ada answers questions about ${d.name}'s sites once you add ${d.name}'s Anthropic API key. Its questions and answers then run on that account, and Anthropic bills it.`}
          </p>
        </div>
      </div>

      <form className="as-form" onSubmit={save}>
        <label className="as-key">
          <FiKey aria-hidden="true" />
          <input
            type="password"
            autoComplete="off"
            spellCheck={false}
            placeholder="sk-ant-…"
            aria-label={`Anthropic API key for ${d.name}`}
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
        </label>
        <button type="submit" className="as-btn as-primary" disabled={busy || !key.trim()}>
          {busy ? "Checking the key…" : d.on ? "Replace key" : "Add key"}
        </button>
        {d.on && (
          <button type="button" className="as-btn" onClick={remove} disabled={busy}>
            Remove key
          </button>
        )}
      </form>
      {msg && <p className={msg.ok ? "as-note" : "as-error"} role="status">{msg.text}</p>}
      <p className="as-hint">
        Every box assigned to {d.name} answers on this key; nothing is set on the boxes. The key is kept on the
        Weavian server, never on a box, and is never shown again once saved.
      </p>
    </section>
  );
}
